import { Grid, Link, ListItemButton, Stack, Typography } from '@mui/material';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { formatNumber, NumberFormatPreset, useCurrency } from '@procureai/ui-mui';
import { secondsToMilliseconds } from 'date-fns';
import { isNotNil, path } from 'ramda';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Union } from 'ts-toolbelt';

import { useTrackSearchResults } from '../../hooks/useTrackSearchResults/useTrackSearchResults.ts';
import { SearchIndexDates, SearchIndexId, SearchIndexSourceRefence } from '../../types/SearchIndex.ts';
import { Target } from '~/hooks/useTrackInteraction/useTrackInteraction';
import { RouteId } from '~/routes/RouteId';
import { eventStopPropagation } from '~/util/function/eventStopPropagation';

export interface SearchHitPurchaseOrderHit extends SearchIndexId, SearchIndexDates, SearchIndexSourceRefence {
  contractItemId: Union.Nullable<string>;
  commodityGroupId: string;
  purchaseRequisitionItemId: string;
  title: string;
  unitId: string;
  currency: string;
  pricePerUnitFloat: number;
  valueFloat: number;
  quantityFloat: Union.Nullable<number>;
  parent: SearchIndexId &
    SearchIndexDates &
    SearchIndexSourceRefence & {
      status: string;
      orderedAtTimestamp: number;
      supplierId: string;
    };
}

interface SearchHitPurchaseOrderProps {
  hit: SearchHitPurchaseOrderHit;
}

const getContract = path(['entity', 'contract']);

export const SearchHitPurchaseOrder = ({ hit }: SearchHitPurchaseOrderProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SearchHitPurchaseOrder' });
  const { format } = useCurrency({ code: hit.currency });
  const navigate = useNavigate();

  const itemLink = RouteId.orderDetail(hit.parent.id);

  const handleItemClicked = useTrackSearchResults({ hit, target: Target.SEARCH_RESULT_PURCHASE_ORDER });

  return (
    <ListItemButton
      onClick={(event) => {
        handleItemClicked(event);
        navigate(itemLink);
      }}
    >
      <Grid container sx={{ flexGrow: 1 }}>
        <Grid size={9}>
          <Link component={RouterLink} to={itemLink} variant="subtitle1" onClick={handleItemClicked} underline="hover">
            {hit.title}
          </Link>
          <Typography
            component="div"
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('date', { createdAt: secondsToMilliseconds(hit.createdAtTimestamp) })}
          </Typography>
          <Stack
            direction="row"
            sx={{
              flexWrap: 'wrap',
              columnGap: 2,
              marginTop: 1,
            }}
          >
            {hit.parent.supplierId ? (
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('supplier')}
                </Typography>
                <Link
                  component={RouterLink}
                  to={RouteId.supplierDetail(hit.parent.supplierId)}
                  variant="body2"
                  onClick={eventStopPropagation}
                >
                  <CuriosityValue ns={CuriosityNamespaceType.SUPPLIER} i18nKey={hit.parent.supplierId} />
                </Link>
              </Stack>
            ) : null}
            {hit.contractItemId ? (
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('contract')}
                </Typography>
                <CuriosityValue
                  ns={CuriosityNamespaceType.CONTRACT_ITEM}
                  i18nKey={hit.contractItemId}
                  valueGetter={getContract}
                >
                  {(contract: any) => (
                    <Link
                      component={RouterLink}
                      to={RouteId.contractDetail(contract.id)}
                      variant="body2"
                      onClick={eventStopPropagation}
                    >
                      {t('contractValue', {
                        label: contract.primarySourceReference,
                        from: contract.validFrom,
                        to: contract.validTo,
                      })}
                    </Link>
                  )}
                </CuriosityValue>
              </Stack>
            ) : null}
            {hit.commodityGroupId ? (
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('commodityGroup')}
                </Typography>
                <Link
                  component={RouterLink}
                  to={RouteId.commodityGroupDetail(hit.commodityGroupId)}
                  variant="body2"
                  onClick={eventStopPropagation}
                >
                  <CuriosityValue ns={CuriosityNamespaceType.COMMODITY_GROUP} i18nKey={hit.commodityGroupId} />
                </Link>
              </Stack>
            ) : null}
          </Stack>
        </Grid>
        <Grid size={3}>
          <Typography
            variant="subtitle1"
            sx={{
              textAlign: 'right',
            }}
          >
            {isNotNil(hit.valueFloat) ? format(hit.valueFloat) : t('emptyValue')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'right',
            }}
          >
            <Trans
              t={t}
              i18nKey="amountUnitPrice"
              values={{
                amount: isNotNil(hit.quantityFloat)
                  ? formatNumber(NumberFormatPreset.DECIMAL, hit.quantityFloat)
                  : t('emptyValue'),
                price: hit.pricePerUnitFloat ? format(hit.pricePerUnitFloat) : t('emptyValue'),
              }}
              components={{
                CuriosityValue: <CuriosityValue ns={CuriosityNamespaceType.UNIT} i18nKey={hit.unitId} />,
              }}
            />
          </Typography>
        </Grid>
      </Grid>
    </ListItemButton>
  );
};
